import { z } from 'zod';
import type { EntityMap, EntityName } from './types.ts';
import { formAnswersSchema, questionFormSchema } from './forms.ts';

/**
 * Sync-Protokoll
 *
 * Clients schreiben jede Änderung als Operation in eine lokale Outbox und
 * schicken sie per `POST /api/sync/push`. Jede Operation hat eine
 * clientseitige `opId`; der Server verarbeitet eine opId genau einmal.
 *
 * Änderungen anderer Geräte holt der Client per `GET /api/sync/pull?cursor=n`.
 * Der Server liefert je geänderter Entität den aktuellen Stand (nicht die
 * einzelnen Operationen) plus den neuen Cursor.
 */

const iso = z.string().min(10);
const id = z.string().min(8).max(64);

export const businessInput = z.object({
  id,
  name: z.string().trim().min(1).max(120),
  color: z.string().max(40).nullable().optional(),
  archivedAt: iso.nullable().optional(),
});

export const projectInput = z.object({
  id,
  businessId: id,
  name: z.string().trim().min(1).max(120),
  color: z.string().max(40).nullable().optional(),
  imageMediaId: id.nullable().optional(),
  view: z.enum(['liste', 'board']).optional(),
  archivedAt: iso.nullable().optional(),
});

export const boardColumnInput = z.object({
  id,
  name: z.string().trim().min(1).max(60),
  color: z.string().max(40).nullable().optional(),
});

export const boardInput = z.object({
  id,
  projectId: id,
  name: z.string().trim().min(1).max(120),
  columns: z.array(boardColumnInput).max(20).optional(),
  archivedAt: iso.nullable().optional(),
});

export const categoryInput = z.object({
  id,
  name: z.string().trim().min(1).max(60),
  color: z.string().max(40).nullable().optional(),
});

export const taskInput = z.object({
  id,
  projectId: id,
  title: z.string().trim().min(1).max(200),
  priority: z.enum(['niedrig', 'normal', 'hoch']).optional(),
  dueAt: iso.nullable().optional(),
  assigneeIds: z.array(id).max(20).optional(),
  categoryIds: z.array(id).max(20).optional(),
  /** Nur „erledigt" (archivieren) und „offen" (wiederherstellen) sind direkt setzbar. */
  status: z.enum(['offen', 'erledigt']).optional(),
  /** Platz auf einem Board; `null` nimmt die Aufgabe von des Boards */
  boardId: id.nullable().optional(),
  columnId: id.nullable().optional(),
  boardOrder: z.number().finite().optional(),
  /** Hauptaufgabe (Unteraufgabe); `null` löst die Verbindung */
  parentId: id.nullable().optional(),
  subtaskOrder: z.number().finite().optional(),
});

export const messageInput = z.object({
  id,
  taskId: id,
  kind: z.enum(['text', 'bild', 'video', 'sprache', 'datei', 'link', 'meilenstein', 'rueckfrage']),
  body: z.string().max(20000).default(''),
  stepRole: z.enum(['problem', 'loesung', 'pruefung']).nullable().optional(),
  checkResult: z.enum(['ok', 'nicht_ok']).nullable().optional(),
  replyToId: id.nullable().optional(),
  replyPoint: z.number().int().min(0).nullable().optional(),
  mediaId: id.nullable().optional(),
  isMilestone: z.boolean().optional(),
  questionTo: id.nullable().optional(),
  questionOpen: z.boolean().optional(),
  form: questionFormSchema.nullable().optional(),
  mentions: z.array(z.string().min(1).max(80)).max(50).optional(),
  forwarded: z.boolean().optional(),
  createdAt: iso.optional(),
});

/** Reaktion: ein Emoji (Graphem mit Bildzeichen), je Person und Nachricht eine. */
export const reactionInput = z.object({
  messageId: id,
  taskId: id,
  emoji: z.string().min(1).max(16).regex(/\p{Extended_Pictographic}/u, 'Nur Emoji').optional(),
});

export const checklistItemInput = z.object({
  id,
  taskId: id,
  text: z.string().trim().min(1).max(300).optional(),
  done: z.boolean().optional(),
  order: z.number().finite().optional(),
});

export const formResponseInput = z.object({
  messageId: id,
  taskId: id,
  answers: formAnswersSchema,
  voiceMediaId: id.nullable().optional(),
  /** Alle referenzierten Medien (Dateien, Sprachantwort), damit der Client auf Uploads wartet */
  mediaIds: z.array(id).max(60).optional(),
});

export const taskUserStateInput = z.object({
  taskId: id,
  pinned: z.boolean().optional(),
  muted: z.boolean().optional(),
  lastReadAt: iso.nullable().optional(),
  starred: z.array(id).max(500).optional(),
});

export const INPUT_SCHEMAS = {
  business: businessInput,
  project: projectInput,
  board: boardInput,
  category: categoryInput,
  task: taskInput,
  message: messageInput,
  taskUserState: taskUserStateInput,
  formResponse: formResponseInput,
  reaction: reactionInput,
  checklistItem: checklistItemInput,
} as const;

export type WritableEntity = keyof typeof INPUT_SCHEMAS;

export const syncOp = z.discriminatedUnion('entity', [
  ...(Object.keys(INPUT_SCHEMAS) as WritableEntity[]).map((entity) =>
    z.object({
      opId: id,
      entity: z.literal(entity),
      op: z.enum(['upsert', 'delete']),
      data: INPUT_SCHEMAS[entity],
    }),
  ),
] as unknown as [z.ZodObject<any>, ...z.ZodObject<any>[]]);

export type SyncOp = {
  [K in WritableEntity]: {
    opId: string;
    entity: K;
    op: 'upsert' | 'delete';
    data: z.input<(typeof INPUT_SCHEMAS)[K]>;
  };
}[WritableEntity];

export const pushRequest = z.object({
  deviceId: z.string().min(4).max(64),
  ops: z.array(syncOp).max(500),
});

export interface PushResult {
  results: Array<{ opId: string; ok: boolean; error?: string }>;
  cursor: number;
}

export type ChangeRow = {
  [K in EntityName]: { entity: K; id: string; deleted: boolean; data: EntityMap[K] | null };
}[EntityName];

export interface PullResult {
  changes: ChangeRow[];
  cursor: number;
  hasMore: boolean;
}

let lastTs = 0;
let seq = 0;

/**
 * UUIDv7: zeitlich sortierbar, clientseitig erzeugbar. Innerhalb derselben
 * Millisekunde zählt ein 12-Bit-Zähler hoch, damit die Reihenfolge der
 * Erzeugung erhalten bleibt (wichtig für Nachrichten im selben Moment).
 */
export function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let ts = Date.now();
  if (ts <= lastTs) {
    seq++;
    if (seq > 0xfff) {
      lastTs++;
      seq = 0;
    }
    ts = lastTs;
  } else {
    lastTs = ts;
    seq = bytes[7]! & 0x3f; // zufälliger, kleiner Startwert lässt Platz zum Hochzählen
  }
  bytes[0] = Math.floor(ts / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(ts / 2 ** 32) & 0xff;
  bytes[2] = (ts >>> 24) & 0xff;
  bytes[3] = (ts >>> 16) & 0xff;
  bytes[4] = (ts >>> 8) & 0xff;
  bytes[5] = ts & 0xff;
  bytes[6] = 0x70 | ((seq >> 8) & 0x0f);
  bytes[7] = seq & 0xff;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
